import { useState, useEffect } from 'react';
import { FileText, CalendarDays, UtensilsCrossed, ChefHat, Image, Settings, BookOpen, Layers, ArrowRight, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CmsDashboard({ navigate }) {
  const [stats, setStats] = useState({ actualites: 0, evenements: 0, carte: 0, plateaux: 0 });
  const [recentActu, setRecentActu] = useState([]);
  const [recentEvts, setRecentEvts] = useState([]);
  const [waEnabled, setWaEnabled] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/site/stats`).then(r => r.json()).then(setStats).catch(() => {});
    fetch(`${API}/api/site/actualites`).then(r => r.json()).then(d => setRecentActu(d.slice(0, 2))).catch(() => {});
    fetch(`${API}/api/site/evenements`).then(r => r.json()).then(d => setRecentEvts(d.slice(0, 2))).catch(() => {});
    fetch(`${API}/api/site/whatsapp-config`).then(r => r.json()).then(d => setWaEnabled(d.enabled !== false)).catch(() => {});
  }, []);

  const statCards = [
    { label: 'Actualites', sub: 'actives', count: stats.actualites, icon: FileText, color: '#e67e22', bg: '#fef3e2', to: 'actualites' },
    { label: 'Evenements', sub: 'a venir', count: stats.evenements, icon: CalendarDays, color: '#8e44ad', bg: '#f3e8fa', to: 'evenements' },
    { label: 'Carte', sub: 'plats', count: stats.carte, icon: UtensilsCrossed, color: '#1a2744', bg: '#e8ecf1', to: 'cartes' },
    { label: 'Plateaux', sub: 'traiteur', count: stats.plateaux, icon: ChefHat, color: '#0d9488', bg: '#e0f5f3', to: 'galerie' },
  ];

  const contentTiles = [
    { label: 'Visuels du site', sub: 'Modifier les photos', icon: Image, to: 'visuels' },
    { label: 'Cartes PDF', sub: 'Upload menus imprimables', icon: BookOpen, to: 'cartes' },
    { label: 'Parametres generaux', sub: 'Infos, horaires, Zenchef', icon: Settings, to: 'settings' },
    { label: 'Carte Restaurant', sub: 'Gerer les plats', icon: UtensilsCrossed, to: 'carte-resto' },
    { label: 'Plateaux Traiteur', sub: 'Gerer les plateaux', icon: Layers, to: 'plateaux' },
    { label: 'Galerie Photos', sub: 'Ajouter des photos', icon: Image, to: 'galerie' },
  ];

  return (
    <div className="space-y-8" data-testid="cms-dashboard">
      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map(s => (
          <button key={s.label} onClick={() => navigate(s.to)}
            className="bg-white border border-gray-200 rounded-lg p-5 text-left hover:shadow-md transition-shadow group"
            data-testid={`stat-${s.label.toLowerCase()}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.bg }}>
                <s.icon size={20} style={{ color: s.color }} />
              </div>
              <ArrowRight size={16} className="text-gray-300 group-hover:text-gray-500 transition-colors mt-1" />
            </div>
            <div className="text-3xl font-bold text-gray-900">{s.count}</div>
            <div className="text-sm text-gray-900 font-medium">{s.label}</div>
            <div className="text-xs text-gray-400">{s.sub}</div>
          </button>
        ))}
      </div>

      {/* Recent items */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FileText size={18} className="text-orange-500" />
              <span className="font-semibold text-gray-900">Actualites recentes</span>
            </div>
            <button onClick={() => navigate('actualites')} className="text-sm text-orange-500 hover:text-orange-600 flex items-center gap-1" data-testid="manage-actualites-btn">
              Gerer <ArrowRight size={14} />
            </button>
          </div>
          {recentActu.length === 0 && <p className="text-sm text-gray-400">Aucune actualite</p>}
          {recentActu.map(a => (
            <div key={a.id} className="py-2 border-b border-gray-100 last:border-0">
              <p className="font-medium text-sm text-gray-900">{a.title}</p>
              <p className="text-xs text-gray-500 line-clamp-1">{a.content}</p>
            </div>
          ))}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CalendarDays size={18} className="text-purple-500" />
              <span className="font-semibold text-gray-900">Evenements a venir</span>
            </div>
            <button onClick={() => navigate('evenements')} className="text-sm text-purple-500 hover:text-purple-600 flex items-center gap-1" data-testid="manage-evenements-btn">
              Gerer <ArrowRight size={14} />
            </button>
          </div>
          {recentEvts.length === 0 && <p className="text-sm text-gray-400">Aucun evenement</p>}
          {recentEvts.map(e => (
            <div key={e.id} className="py-2 border-b border-gray-100 last:border-0">
              <p className="font-medium text-sm text-gray-900">{e.title}</p>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                {e.event_date && <span>📅 {new Date(e.event_date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}</span>}
                {e.event_time && <span>🕐 {e.event_time}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Content tiles */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Gestion du contenu</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {contentTiles.map(t => (
            <button key={t.label} onClick={() => navigate(t.to)}
              className="bg-white border border-gray-200 rounded-lg p-4 text-left hover:shadow-md hover:border-gray-300 transition-all group"
              data-testid={`tile-${t.to}`}>
              <div className="flex items-center gap-3">
                <t.icon size={20} className="text-gray-400 group-hover:text-gray-600" />
                <div>
                  <p className="font-medium text-sm text-gray-900">{t.label}</p>
                  <p className="text-xs text-gray-400">{t.sub}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* WhatsApp quick toggle */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${waEnabled ? 'bg-[#25D366]' : 'bg-gray-300'}`}>
              <MessageCircle size={20} className="text-white" />
            </div>
            <div>
              <p className="font-medium text-sm text-gray-900">Bouton WhatsApp</p>
              <p className="text-xs text-gray-400">{waEnabled ? 'Actif sur le site' : 'Desactive'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={async () => {
                const next = !waEnabled;
                setWaEnabled(next);
                await fetch(`${API}/api/site/whatsapp-toggle`, {
                  method: 'PUT', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ enabled: next })
                });
                toast.success(next ? 'WhatsApp active' : 'WhatsApp desactive');
              }}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${waEnabled ? 'bg-[#25D366]' : 'bg-gray-300'}`}
              data-testid="dashboard-whatsapp-toggle">
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${waEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
            <button onClick={() => navigate('settings')} className="text-xs text-gray-500 hover:text-gray-700" data-testid="whatsapp-settings-link">
              Configurer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
